/* eslint-disable @typescript-eslint/ban-ts-comment */
import moment from 'moment-jalaali';
import Router from 'next/router';
import { isMobile, isIOS } from 'react-device-detect';
import { parseCookies, setCookie as setNookie, destroyCookie } from 'nookies';
import { FundamentalResponseType } from 'typings/fundamental.type';
import { LoginRoute } from 'routes/account.routes'; // eslint-disable-line
import {
  DateType,
  FilterMethodType,
  OptionType,
  QueryType,
  SearchOptionType,
} from 'typings/common.type';
import i18n from 'locales/fa.json';
import { NavRawDataType } from 'typings/nav.type';

// todo: if you need header, searchbar, bottom navigation height use these sizes
export const HEADER_HEIGHT = 56;
export const SEARCHBAR_HEIGHT = 84;
export const TABS_HEIGHT = 43;
export const NAVIGATION_HEIGHT = 56;
export const PAGE_HEIGHT = HEADER_HEIGHT + NAVIGATION_HEIGHT + TABS_HEIGHT;
export const FULL_HEIGHT_TABLE =
  HEADER_HEIGHT + SEARCHBAR_HEIGHT + TABS_HEIGHT + NAVIGATION_HEIGHT;
export const FULL_HEIGHT = HEADER_HEIGHT + SEARCHBAR_HEIGHT + TABS_HEIGHT;

export const INITIAL_UTM = {
  source: undefined,
  medium: undefined,
  referrer: undefined,
};

export const push = (route: string) => (): Promise<boolean> =>
  Router.push(route, undefined, { shallow: true });

export const replace = (route: string) => (): Promise<boolean> =>
  Router.replace(route, undefined, { shallow: true });

export const handleParams = (next?: QueryType, referrer?: QueryType): string =>
  referrer && next
    ? `?next=${next}${next.includes('?') ? '&' : '?'}referrer=${referrer}`
    : next
    ? `?next=${next}`
    : referrer
    ? `?referrer=${referrer}`
    : '';

export const universalAtob = (b64Encoded): string => {
  try {
    return atob(b64Encoded);
  } catch (err) {
    return Buffer.from(b64Encoded, 'base64').toString();
  }
};

export const GetTokenExpireTime = (token: string): Date => {
  const hash = token.split('.');
  const result = JSON.parse(universalAtob(hash[1]) as string).exp;
  return new Date(result * 1000);
};

export const setCookie = (
  name: string,
  value: string,
  params: object = {},
): void => {
  setNookie(null, name, value, { path: '/', ...params });
};

export const getCookie = (name): string => parseCookies()[name];

export const removeCookie = (name): void => {
  destroyCookie(null, name, {
    path: '/',
    domain: process.env.NEXT_PUBLIC_FRONT_DOMAIN,
  });
};

/**
 * Sorts the input array by the given key ascending
 @param {string} key: the sort key
 @returns {T[]}: the sorted array
 */
export const sortBy = <T>(array: T[], key: string): T[] =>
  // eslint-disable-next-line
  // @ts-ignore
  array.sort((a, b) => (a[key] < b[key] ? 1 : a[key] > b[key] ? -1 : 0));

/**
 * Filters the input dictionary by the given conditions on its keys
 @param {{[key: string]: Object}} dict: the input dict (with string keys)
 @param {(key : string) => boolean} cond: the filter condition (inputs the key)
 @returns {{[key: string]: Object}}: the subset of the original dict whose all keys match the filter condition
 */
export const dictFilter = (
  dict: { [key: string]: Record<string, any> | string | number | boolean }, // eslint-disable-line
  condition: (key: string) => boolean, //eslint-disable-line
): object =>
  Object.keys(dict)
    .filter(condition)
    .reduce((obj, key) => ({ ...obj, [key]: dict[key] }), {});

/**
 * result must not be null or undefined
 * if withoutZero=true => 0 is not valid too
 * @param {number | any} number
 * @returns {boolean}
 */
export const isValid = (value, withoutZero = false): boolean =>
  !(
    value === null ||
    value === undefined ||
    value === '' ||
    (withoutZero && value === 0)
  );

/**
 * Sorter for AntTable
 @param key the key for sorting elements
 @returns {number}: for array sort, return +(number),-(number),0
 @example [{key: 10}, {key: 5}, {key: null}]:
 ascend sort => [{key: 5}, {key: 10}, {key: null}]
 descend sort => [{key: 10}, {key: 5}, {key: null}]
 */
export const sorter =
  (key: string, withoutZero = false) =>
  (a, b, sort): number => {
    // if (a[key]) === null|undefined in ascend & descend sort, (a) should be in the end of list
    // withoutZero type => if (a[key]) === 0 in ascend & descend sort, (a) should be in the end of list

    if (!isValid(a[key], withoutZero)) return 1;
    if (!isValid(b[key], withoutZero)) return -1;
    if (a.isTotal || b.isTotal) return 0;

    return sort === 'descend' ? b[key] - a[key] : a[key] - b[key];
  };

export const peSorter = (a, b, sort): number =>
  // for eps less than zero, we should show a '-' and should be end of list in table
  // if (eps > 0 => sort key = eps)
  // if (eps <= 0 => sort key = null)
  sorter('key')(
    { key: a.price_per_eps > 0 ? a.price_per_eps : null },
    { key: b.price_per_eps > 0 ? b.price_per_eps : null },
    sort,
  );

/**
 *  23456.789 => 23,466
 * @param key: number or string
 * @param isRound: boolean
 * @returns {string}
 */
export const fixLocaleString = (key, isRound = true, toFixNum = 0): string => {
  if (key === null || key === undefined) return '';
  const number = Number(key);
  return !number
    ? key
    : (number < 10 && number > -10) || !isRound
    ? !toFixNum
      ? number.toLocaleString('en')
      : number.toFixed(toFixNum)
    : Math.round(key).toLocaleString('en');
};

/**
 * Persian alphabetical sorter for AntTable
 * @param key the key for sorting elements
 */
export const persianSorter =
  (key: string) =>
  (a, b, sort): number => {
    // if (a[key]) === null|undefined in ascend & descend sort, (a) should be in the end of list
    if (!isValid(a[key])) return 1;
    if (!isValid(b[key])) return -1;
    if (a.isTotal || b.isTotal) return 0;

    return sort === 'descend'
      ? a[key]?.localeCompare(b[key], 'fa')
      : b[key]?.localeCompare(a[key], 'fa');
  };

/**
 * return (time format (HH:mm) | date format (YYYY-MM-DD) | both)
 * default return time format (HH:mm)
 * type: time | date
 */
export const getFormatDate = (
  date: string | Date,
  type?: 'time' | 'date',
  formatter?: string,
): string => {
  const dateFormat = 'YYYY-MM-DD';
  const timeFormat = 'HH:mm';
  const format = formatter || (type === 'date' ? dateFormat : timeFormat);
  return moment(date).format(format);
};

/**
 * convert date picker string to object
 * @param {string} : 2021/05/12
 * @returns {object} : {year: 2021, month: 05, day: 12}
 */
export const convertDatePickerStringToObject = (
  date: string,
  sign?: string,
): DateType => {
  if (date) {
    const [year, month, day] = date
      .split(sign || '/')
      .map((item) => parseInt(item, 10));
    return { year, month, day };
  }
  return null;
};

/**
/*
 * Convert dash-separated string to camelCase
 * @param {string} : dash-separated-string
 * @returns {string} : dashSeparatedString
 */
export const camelCase = (input: string): string =>
  input &&
  input !== ' ' &&
  (input.slice(0, 1).toLowerCase() + input.slice(1))
    .replace(/([-_ ]){1,}/g, ' ')
    .split(/[-_ ]/)
    .reduce((cur, acc) => cur + acc[0].toUpperCase() + acc.substring(1));

export const snakeCase = (str: string): string =>
  str?.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

// it is checked whether the string has a Persian character or not
export const isPersian = (str: string): RegExpMatchArray => {
  const Persian = /[آ-ی]/;
  return str?.match(Persian);
};

// eslint-disable-next-line
export const promisedDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  // eslint-disable-next-line
): ((...args: Parameters<T>) => Promise<any>) => {
  let timer: NodeJS.Timeout;
  return (...args) => {
    clearTimeout(timer);
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(func(...args)), delay);
    });
  };
};

export const kebabCase = (input: string): string =>
  input
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();

export const kebabToSnake = (input: string): string => input.replace(/-/g, '_');

// convert camelCase date to Snake
export const camelToSnake = (string): string =>
  string.replace(/[\w]([A-Z])/g, (m) => `${m[0]}_${m[1]}`).toLowerCase();

// convert Gregorian date to jalaali
export const convertToJalaliDate = (date: string | Date): string =>
  (date && moment(date).locale('en').format('jYYYY/jMM/jDD').trim()) || null;

// convert jalaali date to Gregorian
export const convertJalaliToGregorian = (date: string): string =>
  (date &&
    moment(date).isValid() &&
    moment(date, 'jYYYY/jjM/jjD').locale('en').format('YYYY-MM-DD').trim()) ||
  null;

export const checkDateRange = (start: string, end: string): boolean => {
  if (!start || !end) return true;
  return new Date(end).getTime() > new Date(start).getTime();
};

export const minLengthMessage = (length: number): string =>
  `${i18n.account.minLength1} ${length} ${i18n.account.minLength2}`;

export const maxLengthMessage = (length: number): string =>
  `${i18n.account.maxLength1} ${length} ${i18n.account.minLength2}`;

export const lessThanMessage = (length: number): string =>
  `${i18n.account.lessThan} ${length} ${i18n.account.minLength2}`;

export const moreThanMessage = (length: number): string =>
  `${i18n.account.moreThan} ${length} ${i18n.account.minLength2}`;

export const redirectToLoginPage = (isManualLogout = false): void => {
  let nextPath = isManualLogout ? null : Router.router.asPath;
  if (Router.router.query.actionSheet)
    nextPath = nextPath?.replace(
      `?actionSheet=${Router.router.query.actionSheet}` ||
        `&actionSheet=${Router.router.query.actionSheet}`,
      '',
    );
  const loginPath = LoginRoute(nextPath);
  if (!nextPath?.includes(LoginRoute())) replace(loginPath)();
};

export const selectFileName = (uri: string): string => {
  const lastslashindex = uri.split('/');
  return lastslashindex[lastslashindex.length - 1];
};

export const arabicToPersian = (value: string): string =>
  value?.replace(/ي/g, 'ی').replace(/ك/g, 'ک') || value;

export const persianToArabic = (value: string): string =>
  value?.replace(/ی/g, 'ي').replace(/ک/g, 'ك') || value;

/**
 *  1234567890000 => "1234 B"
 *  123456789 => "123 M"
 *  123456 => "123456"
 * @param {string | number} number
 * @param {number} decimalPlaces
 * @returns {string}
 */
export const toSummarize = (
  number: number,
  decimalPlaces = 0,
): string | number => {
  if (typeof number !== 'number') return '';

  const value = Number(number);
  const biggerThanOneBillion = Math.abs(value) >= 1e9;
  const biggerThanOneMillion = Math.abs(value) >= 1e6;
  const denominator = biggerThanOneBillion
    ? 1e9
    : biggerThanOneMillion
    ? 1e6
    : 1;
  const result = Number((value / denominator).toFixed(decimalPlaces));

  if (biggerThanOneBillion) return `${result.toLocaleString('en')} B`;
  if (biggerThanOneMillion) return `${result.toLocaleString('en')} M`;
  return value.toLocaleString('en');
};

export const valueToPercent = (data: number[]): number[] => {
  const total = data.reduce((a, b) => a + b, 0);
  return data.map((item) => Math.round((item / total) * 100));
};
export const differDate = (
  date: string | Date,
  baseDate?: string | Date,
): number => {
  const fromDate = moment(baseDate) || moment();
  return fromDate.diff(date, 'days', true);
};

/**
 * convert date picker object to string
 * @param {object} : {year: 2021, month: 05, day: 12}
 * @returns {string} : 2021/05/12
 */
export const convertDatePickerObjectToString = (date: DateType): string =>
  date ? `${date?.year}/${date?.month}/${date?.day}` : '';

const getDifferDate = (date: string, baseDate?: string | Date): number =>
  differDate(convertJalaliToGregorian(date), baseDate);

export const convertNumber = (
  value: string | number,
  locale = 'en',
): string => {
  if (value === null || value === undefined) return '';
  const toFaDict = {
    0: '۰',
    1: '۱',
    2: '۲',
    3: '۳',
    4: '۴',
    5: '۵',
    6: '۶',
    7: '۷',
    8: '۸',
    9: '۹',
  };
  const toEnDict = {
    '۰': 0,
    '۱': 1,
    '۲': 2,
    '۳': 3,
    '۴': 4,
    '۵': 5,
    '۶': 6,
    '۷': 7,
    '۸': 8,
    '۹': 9,
  };
  const numberDict = {
    fa: toFaDict,
    en: toEnDict,
  };

  const letters = value.toString().split('');
  const arr = letters.map((item) =>
    item in numberDict[locale] ? numberDict[locale][item] : item,
  );
  return arr.join('');
};

export const filterMethod = <T = object>({
  data,
  tableTitle,
  searchValue,
  analysisFilter,
  selectedIndustries,
  selectedReportDates,
  showOpenSymbols,
  groupByIndustryStatus,
  startDate,
  endDate,
  optionType,
  searchType = 'word',
}: FilterMethodType): Record<string, T[]> => {
  let filteredData = data;

  const searchCondition = (item): boolean =>
    searchValue
      ? searchValue === item.symbol_12chars || searchValue === item.symbol
      : true;

  const optionCondition = (item): boolean =>
    optionType
      ? optionType === 'all'
        ? true
        : item.option_type === optionType
      : true;

  const analysisCondition = (item): boolean =>
    !analysisFilter
      ? true
      : analysisFilter === 'all'
      ? true
      : analysisFilter === 'admin'
      ? item.is_default
      : !item.is_default;

  const industriesCondition = (item): boolean => {
    if (!selectedIndustries || selectedIndustries.length === 0) {
      return true;
    }

    const isIndustryMatched = selectedIndustries.includes(item.industry);
    const isIndustryCodeMatched = selectedIndustries.some(
      (code) => code === String(item.industry_code),
    );

    return isIndustryMatched || isIndustryCodeMatched;
  };

  const reportDateCondition = (item: { report_date: string }): boolean =>
    selectedReportDates
      ? selectedReportDates.includes(convertToJalaliDate(item.report_date))
      : true;

  const openSymbolsCondition = (item): boolean =>
    !showOpenSymbols ? true : item.state?.includes('مجاز');

  const searchbyChar = (item): boolean =>
    searchValue
      ? item.symbol?.match(convertNumber(searchValue)) ||
        item.name?.match(convertNumber(searchValue)) ||
        item.underlying_asset_symbol?.match(convertNumber(searchValue))
      : true;

  const dateCondition = (item): boolean =>
    startDate && !endDate
      ? getDifferDate(startDate, item.expiration_date) >= 0
      : !startDate && endDate
      ? getDifferDate(endDate, item.expiration_date) <= 0
      : startDate && endDate
      ? getDifferDate(startDate, item.expiration_date) >= 0 &&
        getDifferDate(endDate, item.expiration_date) <= 0
      : true;

  filteredData = data.filter(
    (item) =>
      reportDateCondition(item) &&
      industriesCondition(item) &&
      analysisCondition(item) &&
      openSymbolsCondition(item) &&
      dateCondition(item) &&
      optionCondition(item) &&
      (searchType === 'word' ? searchCondition(item) : searchbyChar(item)),
  );

  if (groupByIndustryStatus && filteredData.length) {
    const industriesDict: Record<string, T[]> = Object.assign(
      {},
      ...filteredData.map((item) => ({ [item.industry]: [] })),
    );

    filteredData?.forEach((item) => {
      industriesDict?.[item.industry]?.push(item);
    });

    return industriesDict;
  }
  return filteredData?.length ? { [tableTitle || 'data']: filteredData } : null;
};

export const normalizeSearchOptions = (data): { id: string; label: string }[] =>
  data.map((item) => ({
    id: item.symbol_12chars,
    label: `${item.symbol} - ${item.name}`,
  }));

export function getQueryParams(string: string): { [key: string]: string } {
  if (!string.matchAll) return {};
  const regexp = /(\?|&)([^=]+)=([^&]+)/g;
  const arr = [...(string.matchAll(regexp) || [])];
  return arr.reduce((obj, item) => {
    // eslint-disable-next-line
    obj[item[2]] = item[3];
    return obj;
  }, {});
}
// check empty object
export const isEmptyObj = (obj: object): boolean =>
  obj ? !Object.keys(obj).length : !obj;

// load scenario list with edit and delete option
export const loadAllScenarios = (
  data: NavRawDataType | FundamentalResponseType,
): Record<string, { name: string; id: string }[]> => {
  const scenarios = {};
  Object.keys(data?.all_report_dates).forEach((item) => {
    scenarios[convertToJalaliDate(item)] = data.all_report_dates[
      item
    ].scenario_names.map((scenario) => {
      const isDefaultScenario =
        !scenario.name || scenario.name === i18n.global.combine;
      return {
        hasDefault: data.all_report_dates[item].has_default_analysis,
        isDefault: isDefaultScenario,
        id: scenario.id,
        name: scenario.name || i18n.global.defaultAnalysis,
        hasEdit: !isDefaultScenario,
        hasDelete: !isDefaultScenario,
        icon: !isDefaultScenario ? 'icon-user' : 'icon-enigma',
      };
    });
  });

  return scenarios;
};

export const loadAllReportDates = (
  data: NavRawDataType | FundamentalResponseType,
): { id: string; name: string }[] =>
  Object.keys(data.all_report_dates).map((item) => ({
    id: convertToJalaliDate(item),
    name: `${i18n.global.financialYear + convertToJalaliDate(item)}`,
    hasEdit: false,
    hasDelete: false,
    hasDefault: data.all_report_dates[item].has_default_analysis,
  }));

// convert utc date to simple date time
export const convertDateTime = (
  value: string | Date,
  formatter?: string,
): string =>
  moment(value, 'YYYY-MM-DD HH:mm:ss')
    .locale('en')
    .format(formatter || 'jYYYY/jMM/jDD HH:mm:ss');

/**
 * 1234567890 => "1234 M"
 * @param  {string | number} number: number or string
 * @returns {string}
 */
export const toMillion = (number): string => {
  if (number === null || number === undefined) return '';
  const value = parseInt(number, 10);
  if (Math.abs(value) >= 1e6)
    return `${Math.round(value / 1e6).toLocaleString('en')} M`;
  return value.toLocaleString('en');
};

export const SECURITY_TYPES = {
  STOCK: 1,
  OUT_OF_EXCHANGE_STOCK: 2,
  BOND: 3,
  OPTION: 4,
  FUND: 5,
  FUTURE: 6,
  COMMODITY: 7,
  LOAN: 8,
  INTANGIBLE: 9,
  STOCK_RIGHT: 10,
  OUT_OF_EXCHANGE_FUND: 11,
  TWEAKED_OPTION: 12,
};

export const generateAllOptions = (
  options: SearchOptionType[],
  isOption: boolean,
): SearchOptionType[] =>
  options
    .map((item) => {
      const security = { ...item, type: 'security' };
      const shareholders = item.security_type === SECURITY_TYPES.STOCK && {
        ...item,
        type: 'shareholders',
      };
      const fundamental = item.is_fundamental && {
        ...item,
        type: 'fundamental',
      };
      const nav = item.is_investing ? { ...item, type: 'nav' } : null;
      const chain = item.is_option_underlying_asset
        ? { ...item, type: 'chain' }
        : null;
      const strategy = item.is_option_underlying_asset
        ? { ...item, type: 'strategy' }
        : null;

      const items = isOption
        ? [chain, strategy]
        : [security, fundamental, nav, shareholders, chain, strategy];

      return items.filter((k) => k);
    })
    .flat();

export const getSearchOptions = (data): OptionType[] => {
  const options = (data || []).map((item) => ({
    label: `${item.symbol} - ${item.security_name}`,
    id: item.id,
    name: item.symbol,
  }));
  const map = new Map(options.map((pos) => [pos.name, pos]));

  return [...map.values()] as OptionType[];
};

export const faNumbersToEn = (value: string): string => {
  if (value === null || value === undefined) return '';
  const toEnDict = {
    '۰': 0,
    '۱': 1,
    '۲': 2,
    '۳': 3,
    '۴': 4,
    '۵': 5,
    '۶': 6,
    '۷': 7,
    '۸': 8,
    '۹': 9,
  };
  const letters = value.toString().split('');
  const arr = letters.map((item) => (item in toEnDict ? toEnDict[item] : item));
  return arr.join('');
};

export const getRecaptcha = async (
  executeRecaptcha: (action: string) => Promise<string>,
): Promise<string> => {
  const isCypress = typeof window !== 'undefined' && !!(window as any).Cypress; // eslint-disable-line
  const recaptcha = isCypress ? 'a' : await executeRecaptcha('OTP');
  return recaptcha;
};

export const waitForElm = (selector: string): Promise<Element> =>
  new Promise((resolve): void => {
    if (document.querySelector(selector)) {
      resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });

export const googleTagManagerScript = (gtmId: string): string =>
  `(function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "${gtmId}");`;

// handles the animation when scrolling to the top
export const scrollToTop = (isVisible?: boolean, windowScroll = true): void => {
  // check isTable flag so decide to set scrollTo on table component or window.
  const currentElement = document.getElementsByClassName(
    'ReactVirtualized__Grid',
  );
  if (isVisible) {
    currentElement?.[0]?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (windowScroll) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }
};

export const addTooman = (value: number): string =>
  `${fixLocaleString(value)} ${i18n.global.tooman}`;

const goftionKeyTrigger = (): void => {
  // @ts-ignore
  Goftino.open();
};

const goftinoReady = (): void => {
  document
    .getElementById('goftino-button')
    ?.addEventListener('click', goftionKeyTrigger);

  // @ts-ignore
  Goftino.setWidget({
    hasIcon: false,
  });
};

// These settings are for the goftino online chat support system
export const goftinoScript = (): void => {
  ((): void => {
    const a = window;
    const d = document;
    function gsc(): void {
      const g = d.createElement('script');
      const s = 'https://www.goftino.com/widget/rzIlQn';
      const l = localStorage.getItem('goftino');
      g.id = 'goftino-script';
      a.addEventListener('goftino_ready', goftinoReady);
      (g.type = 'text/javascript'),
        (g.async = !0),
        (g.referrerPolicy = 'no-referrer-when-downgrade'),
        (g.src = l ? `${s}?o=${l}` : s);
      d.body.appendChild(g);
    }
    d.readyState === 'complete'
      ? gsc()
      : // @ts-ignore
      a.attachEvent
      ? // @ts-ignore
        a.attachEvent('onload', g)
      : a.addEventListener('load', gsc, !1);
  })();
};

// remove listeners and tags of goftino from body
export const unmountGoftino = (): void =>
  ((): void => {
    window.removeEventListener('goftino_ready', goftinoReady);
    document
      .getElementById('goftino-button')
      ?.removeEventListener('click', goftionKeyTrigger);
    const script = document.getElementById('goftino-script');
    const iframe = document.getElementById('goftino_w');
    script && document.body.removeChild(script);
    iframe && document.body.removeChild(iframe);
  })();

export const isLandscape = (): boolean => {
  if (window.screen?.orientation) {
    return isMobile && window.screen.orientation.type.includes('landscape');
  }
  return isMobile && !window.matchMedia('(orientation: portrait)').matches;
};

export const isIosPwa = (): boolean =>
  isIOS &&
  'standalone' in window.navigator &&
  window.navigator.standalone === true &&
  window.matchMedia('(display-mode: standalone)').matches;

export const containsNumber = (str: string): boolean => {
  // Regular expression to check if the string contains a number
  const regex = /\d/;
  return regex.test(str);
};

export const containsLetters = (str: string): boolean => {
  if (typeof str !== 'string') return false; // should return false for "undefined"
  // Regular expression to check if the string contains any letter from 'a' to 'z'
  const regex = /[a-zA-Z]/;
  return regex.test(str);
};

type Procedure = (...args: any[]) => void; // eslint-disable-line
export const debounce = <F extends Procedure>(
  func: F,
  delay: number,
): ((...args: Parameters<F>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<F>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const uniqueArrayOfObjects = <T>(array: T[]): T[] =>
  [...new Set(array.map((item) => JSON.stringify(item)))].map((item) =>
    JSON.parse(item),
  );

export const appendParamsToRoute = (
  path: string,
  queryParams: object = {},
): string => {
  const queryString = Object.entries(queryParams)
    .filter(([, value]) => isValid(value)) // Ensure only valid values are included
    .map(([key, value]) => `${key}=${value}`) // Avoid encoding `/`
    .join('&');

  return `${path}${queryString ? `?${queryString}` : ''}`;
};

type FlattenedObject = Record<string, any>; // eslint-disable-line
export const flattenObject = (obj: FlattenedObject): FlattenedObject => {
  const flattened: FlattenedObject = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Recursively flatten nested objects
        Object.assign(flattened, flattenObject(obj[key]));
      } else {
        // Add the current key-value pair to the flattened object
        flattened[key] = obj[key];
      }
    }
  }

  return flattened;
};
